import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import AuthService from "../../../api/services/AuthService";
import { resolveAvatarImageUrl } from "../../../utils/avatarUtils";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import { DISABLE_ALL_NETWORK } from "../../../networkConfig";
import { isApiSuccess, pickAvatarPathFromResponse, DEFAULT_AVATAR_SRC } from "../../../utils/avatarUtils";
import {
  formatNicknameForDisplay,
  getNicknameValidationError,
  NICKNAME_MAX_LENGTH,
  normalizeNicknameForApi,
} from "../../../utils/nicknameUtils";
import AvatarCropPanel from "./AvatarCropModal";

const ALERT_PREFS_KEY = "agce_alert_preferences";

const SYSTEM_TOGGLES = [
  { id: "account", label: "Account Notifications" },
  { id: "asset", label: "Asset Management" },
  { id: "trade", label: "Trade" },
  { id: "earn", label: "Earn" },
  { id: "copy", label: "Copy Trading" },
  { id: "other", label: "Other" },
];

const EXTRA_SECTIONS = [
  { id: "activities", title: "Activities", keys: [{ id: "a1", label: "Activity summary" }, { id: "a2", label: "Device alerts" }] },
  { id: "newlyListed", title: "Newly Listed", keys: [{ id: "n1", label: "New listings" }, { id: "n2", label: "Listing reminders" }] },
  { id: "agcePay", title: "AGCX Pay", keys: [{ id: "p1", label: "Payment updates" }, { id: "p2", label: "Security alerts" }] },
  { id: "market", title: "Market Alerts", keys: [{ id: "m1", label: "Price alerts" }, { id: "m2", label: "Volatility" }] },
  { id: "moments", title: "Moments", keys: [{ id: "mo1", label: "Mentions" }, { id: "mo2", label: "Replies" }] },
  { id: "prediction", title: "Prediction", keys: [{ id: "pr1", label: "Prediction outcomes" }, { id: "pr2", label: "Market events" }] },
];

const PRESET_AVATARS = Array.from({ length: 25 }, (_, i) => {
  const n = i + 1;
  const gender = n <= 12 ? "male" : "female";
  /** PNG uploads are accepted more often than SVG. */
  return {
    id: `av-${n}`,
    gender,
    src: `https://api.dicebear.com/7.x/avataaars/png?seed=${encodeURIComponent(`agce-avatar-${n}`)}&size=256`,
  };
});

/** ~5 MiB guard before hitting the API. */
const AVATAR_UPLOAD_MAX_BYTES = 5 * 1024 * 1024;

function isLikelyAllowedAvatarFile(file) {
  const t = (file?.type || "").toLowerCase().trim();
  if (/^image\/(jpeg|pjpeg|jpg|png|gif|webp)$/i.test(t)) return true;
  if (/^image\/(x-ms-bmp|bmp)$/i.test(t)) return true;
  /** Some browsers/OS report empty type or octet-stream for dragged or renamed files — fall back on extension. */
  if (!t || t === "application/octet-stream") return /\.(jpe?g|png|gif|webp)$/i.test(file?.name || "");
  return false;
}

const defaultAlertState = () => {
  const system = SYSTEM_TOGGLES.reduce((acc, { id }) => {
    acc[id] = true;
    return acc;
  }, {});
  const extras = {};
  EXTRA_SECTIONS.forEach((sec) => {
    extras[sec.id] = sec.keys.reduce((acc, k) => {
      acc[k.id] = true;
      return acc;
    }, {});
  });
  return { system, ...extras };
};

function mergeAlertChannel(patch) {
  const base = defaultAlertState();
  if (!patch || typeof patch !== "object") return base;
  const out = { ...base };
  Object.keys(base).forEach((k) => {
    if (typeof base[k] === "object" && base[k] !== null) {
      out[k] = { ...base[k], ...(patch[k] || {}) };
    }
  });
  return out;
}

function loadAlertPrefs() {
  try {
    const raw = localStorage.getItem(ALERT_PREFS_KEY);
    if (!raw) return { message: defaultAlertState(), email: defaultAlertState() };
    const parsed = JSON.parse(raw);
    return {
      message: mergeAlertChannel(parsed.message),
      email: mergeAlertChannel(parsed.email),
    };
  } catch {
    return { message: defaultAlertState(), email: defaultAlertState() };
  }
}

/** Maps accordion `system` toggle ids ⇄ API `systemNotifications` camelCase keys. */
const UI_SYSTEM_TO_API = {
  account: "accountNotifications",
  asset: "assetManagement",
  trade: "trade",
  earn: "earn",
  copy: "copyTrading",
  other: "other",
};

function truthyAlertFlag(v) {
  return v === true || v === 1 || v === "true" || v === "1";
}

function readApiAlertSubdoc(root, ...aliases) {
  if (!root || typeof root !== "object") return {};
  for (const a of aliases) {
    const o = root[a];
    if (o && typeof o === "object" && !Array.isArray(o)) return o;
  }
  return {};
}

/** One channel (`messages` or `email` from GET) → UI prefs shape (`system`, `activities`, …). */
function apiAlertChannelToUIPrefs(apiSlice) {
  const base = defaultAlertState();
  if (!apiSlice || typeof apiSlice !== "object") return base;

  const sn = apiSlice.systemNotifications;
  if (sn && typeof sn === "object") {
    SYSTEM_TOGGLES.forEach(({ id }) => {
      const ak = UI_SYSTEM_TO_API[id];
      base.system[id] = truthyAlertFlag(sn[ak]);
    });
  }

  const act = readApiAlertSubdoc(apiSlice, "activities");
  base.activities.a1 = truthyAlertFlag(act.activitySummary);
  base.activities.a2 = truthyAlertFlag(act.deviceAlert);

  const nl = readApiAlertSubdoc(apiSlice, "newListed", "newlyListed");
  base.newlyListed.n1 = truthyAlertFlag(nl.newListings);
  base.newlyListed.n2 = truthyAlertFlag(nl.listingReminder);

  const ag = readApiAlertSubdoc(apiSlice, "agcePlay", "agcePay");
  base.agcePay.p1 = truthyAlertFlag(ag.paymentUpdates);
  base.agcePay.p2 = truthyAlertFlag(ag.secirtyAlerts ?? ag.securityAlerts);

  const ma = readApiAlertSubdoc(apiSlice, "marketAlerts", "market");
  base.market.m1 = truthyAlertFlag(ma.priceAlerts);
  base.market.m2 = truthyAlertFlag(ma.volatility);

  const mom = readApiAlertSubdoc(apiSlice, "moments");
  base.moments.mo1 = truthyAlertFlag(mom.mentions);
  base.moments.mo2 = truthyAlertFlag(mom.replies);

  const pr = readApiAlertSubdoc(apiSlice, "prediction");
  base.prediction.pr1 = truthyAlertFlag(pr.predictionOutcomes);
  base.prediction.pr2 = truthyAlertFlag(pr.marketEvents);

  return base;
}

/** UI prefs shape → POST body `{ messages | email }` slice (API naming). */
function uiPrefsToApiAlertChannel(ui) {
  const u = mergeAlertChannel(ui);
  const sn = {};
  SYSTEM_TOGGLES.forEach(({ id }) => {
    sn[UI_SYSTEM_TO_API[id]] = !!u.system[id];
  });
  return {
    systemNotifications: sn,
    activities: {
      activitySummary: !!u.activities.a1,
      deviceAlert: !!u.activities.a2,
    },
    newListed: {
      newListings: !!u.newlyListed.n1,
      listingReminder: !!u.newlyListed.n2,
    },
    agcePlay: {
      paymentUpdates: !!u.agcePay.p1,
      secirtyAlerts: !!u.agcePay.p2,
    },
    marketAlerts: {
      priceAlerts: !!u.market.m1,
      volatility: !!u.market.m2,
    },
    moments: {
      mentions: !!u.moments.mo1,
      replies: !!u.moments.mo2,
    },
    prediction: {
      predictionOutcomes: !!u.prediction.pr1,
      marketEvents: !!u.prediction.pr2,
    },
  };
}

function alertSettingsPickData(res) {
  if (!res || typeof res !== "object") return null;
  /** Body is `{ success, data: { messages, email, … } }` from axios `response.data`. */
  let inner = res.data;
  if (!inner || typeof inner !== "object") return null;
  if (
    inner.data &&
    typeof inner.data === "object" &&
    inner.data.messages &&
    inner.data.email
  ) {
    inner = inner.data;
  }
  if (
    inner.messages &&
    inner.email &&
    typeof inner.messages === "object" &&
    typeof inner.email === "object" &&
    !Array.isArray(inner.messages) &&
    !Array.isArray(inner.email)
  ) {
    return { messages: inner.messages, email: inner.email };
  }
  return null;
}

function useModalBodyLock(open) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);
}

function SettingsModalShell({ open, onClose, title, subtitle, children, footer, modalClassName = "" }) {
  useModalBodyLock(open);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="settings-modal-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className={["settings-modal", modalClassName].filter(Boolean).join(" ")}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="settings-modal__close" aria-label="Close" onClick={onClose}>
          ×
        </button>
        <h2 className="settings-modal__title">{title}</h2>
        {subtitle ? <p className="settings-modal__subtitle">{subtitle}</p> : null}
        <div className="settings-modal__body">{children}</div>
        {footer ? <div className="settings-modal__footer">{footer}</div> : null}
      </div>
    </div>,
    document.body
  );
}

export function ChangeNicknameModal({
  open,
  onClose,
  initialNickname,
  onSaved,
  onNicknameCommitted,
  setUserDetails,
}) {
  const [value, setValue] = useState(initialNickname);

  useEffect(() => {
    if (open) {
      setValue(formatNicknameForDisplay(initialNickname));
    }
  }, [open, initialNickname]);

  const trimmed = value.trim();
  const normalizedNickname = normalizeNicknameForApi(trimmed);
  const validationError = getNicknameValidationError(value, initialNickname);
  const canSave = !validationError;

  const handleSave = async () => {
    if (!canSave) return;
    const nicknameToSave = normalizedNickname;
    if (DISABLE_ALL_NETWORK) {
      setUserDetails((prev) => ({ ...prev, firstName: nicknameToSave, nickname: nicknameToSave }));
      alertSuccessMessage("Nickname updated (offline demo).");
      onNicknameCommitted?.(nicknameToSave);
      onSaved?.();
      onClose();
      return;
    }
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.editNicknameSetting(nicknameToSave);
      if (result?.success === true) {
        const d = result?.data ?? {};
        const nextNick = String(d.nickname ?? d.data?.nickname ?? nicknameToSave).trim();
        setUserDetails((prev) => ({ ...prev, nickname: nextNick, firstName: nextNick }));
        onNicknameCommitted?.(nextNick);
        alertSuccessMessage(result?.message || "Nickname updated.");
        await onSaved?.();
        onClose();
      } else if (result?.message) {
        alertErrorMessage(result.message);
      } else {
        alertErrorMessage("Could not update nickname.");
      }
    } catch {
      alertErrorMessage("Could not update nickname.");
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const footer = (
    <>
      <button type="button" className="settings-modal__btn settings-modal__btn--muted" onClick={onClose}>
        Cancel
      </button>
      <button
        type="button"
        className="settings-modal__btn settings-modal__btn--primary"
        disabled={!canSave}
        title={validationError || undefined}
        onClick={handleSave}
      >
        Save
      </button>
    </>
  );

  return (
    <SettingsModalShell
      open={open}
      onClose={onClose}
      title="Change Nickname"
      subtitle="Change how your name appears on your account."
      modalClassName="settings-modal--nickname"
      footer={footer}
    >
      <label className="settings-modal__label" htmlFor="settings-nickname-input">
        Nickname
      </label>
      <div className="settings-modal__input-wrap">
        <input
          id="settings-nickname-input"
          className="settings-modal__input"
          value={value}
          onChange={(e) => {
            const next = e.target.value;
            if (normalizeNicknameForApi(next).length <= NICKNAME_MAX_LENGTH) {
              setValue(next);
            }
          }}
          maxLength={NICKNAME_MAX_LENGTH + 16}
          autoComplete="nickname"
        />
        {value ? (
          <button
            type="button"
            className="settings-modal__input-clear"
            aria-label="Clear"
            onClick={() => setValue("")}
          >
            <i className="ri-close-circle-fill" aria-hidden="true" />
          </button>
        ) : null}
      </div>
      <p className="settings-modal__hint">
        Use 3–30 characters. Letters, numbers, spaces, and underscores are allowed.
      </p>
      {validationError && trimmed ? (
        <p className="settings-modal__error">{validationError}</p>
      ) : null}
    </SettingsModalShell>
  );
}

export function EditAvatarModal({ open, onClose, currentAvatarUrl, onSaved, onAvatarCommitted, setUserDetails }) {
  const [pickerTab, setPickerTab] = useState("gallery");
  const [filter, setFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState("");
  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState("");
  const [cropBusy, setCropBusy] = useState(false);
  const fileInputRef = useRef(null);
  const cropPanelRef = useRef(null);
  const prevAvatarOpenRef = useRef(false);
  const currentAvatarUrlRef = useRef(currentAvatarUrl);
  currentAvatarUrlRef.current = currentAvatarUrl;

  const list = useMemo(() => {
    if (filter === "male") return PRESET_AVATARS.filter((a) => a.gender === "male");
    if (filter === "female") return PRESET_AVATARS.filter((a) => a.gender === "female");
    return PRESET_AVATARS;
  }, [filter]);

  const clearUploadPreview = useCallback(() => {
    setUploadPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return "";
    });
  }, []);

  const clearCropSrc = useCallback(() => {
    setCropSrc((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return "";
    });
    setCropOpen(false);
    setCropBusy(false);
  }, []);

  useEffect(() => {
    if (!open) {
      prevAvatarOpenRef.current = false;
      setUploadFile(null);
      clearUploadPreview();
      clearCropSrc();
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    if (!prevAvatarOpenRef.current) {
      prevAvatarOpenRef.current = true;
      const url = currentAvatarUrlRef.current;
      const match = PRESET_AVATARS.find((a) => a.src === url);
      setSelectedId(match?.id || PRESET_AVATARS[0].id);
      setFilter("all");
      setPickerTab("gallery");
      setUploadFile(null);
      clearUploadPreview();
      clearCropSrc();
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [open, clearUploadPreview, clearCropSrc]);

  const selected = PRESET_AVATARS.find((a) => a.id === selectedId) || PRESET_AVATARS[0];
  const normalizeAvatarCompareUrl = (url) => String(url ?? "").split("?")[0].split("#")[0].trim();
  const presetChanged =
    selected && normalizeAvatarCompareUrl(selected.src) !== normalizeAvatarCompareUrl(currentAvatarUrl);
  const uploadReady = Boolean(uploadFile);
  const canSave = (pickerTab === "gallery" && presetChanged) || (pickerTab === "upload" && uploadReady);
  const livePreviewSrc =
    pickerTab === "upload" && uploadPreviewUrl
      ? uploadPreviewUrl
      : pickerTab === "gallery"
        ? selected?.src
        : currentAvatarUrl || DEFAULT_AVATAR_SRC;

  const saveDisabledHint = useMemo(() => {
    if (canSave) return "";
    if (pickerTab === "upload" && !uploadFile) return "Choose an image first, then tap Save.";
    if (pickerTab === "gallery" && !presetChanged)
      return "Pick a different gallery avatar or switch to Upload with a new file.";
    return "";
  }, [canSave, pickerTab, uploadFile, presetChanged]);

  const applyAvatarSuccess = async (result, fallbackDisplaySrc, relativePathOrNull) => {
    let rel = String(relativePathOrNull ?? pickAvatarPathFromResponse(result) ?? "").trim();

    if (!rel) {
      try {
        const fresh = await AuthService.getAvatarSetting();
        rel = pickAvatarPathFromResponse(fresh);
      } catch {
        // Keep optimistic fallback below when refetch fails.
      }
    }

    const storedPicture = rel || fallbackDisplaySrc;
    setUserDetails((prev) => ({
      ...prev,
      profilepicture: storedPicture,
      profilePicture: storedPicture,
      ...(rel ? { avatar: rel } : {}),
    }));

    if (rel) {
      onAvatarCommitted?.(rel);
    } else if (
      fallbackDisplaySrc &&
      !String(fallbackDisplaySrc).startsWith("blob:")
    ) {
      onAvatarCommitted?.(fallbackDisplaySrc);
    } else {
      onAvatarCommitted?.(null);
    }

    alertSuccessMessage(result?.message || "Avatar updated.");
    await onSaved?.();
    onClose();
  };

  const resolveAvatarPathAfterUpload = async (result) => {
    let rel = pickAvatarPathFromResponse(result);
    if (rel) return rel;
    try {
      const fresh = await AuthService.getAvatarSetting();
      rel = pickAvatarPathFromResponse(fresh);
      if (rel) return rel;
    } catch {
      // Fall through to legacy endpoint refetch below.
    }
    return "";
  };

  const uploadAvatarFormData = async (fd) => {
    let result = await AuthService.changeAvatarSetting(fd);
    if (!isApiSuccess(result)) {
      // Do not bypass once-per-day via legacy edit-avatar
      if (result?.code === "AVATAR_RATE_LIMITED") {
        return result;
      }
      const msg = String(result?.message || "").toLowerCase();
      if (msg.includes("once a day")) {
        return result;
      }
      const legacyResult = await AuthService.editavatar(fd);
      if (isApiSuccess(legacyResult)) {
        result = legacyResult;
      } else if (legacyResult?.code === "AVATAR_RATE_LIMITED" || String(legacyResult?.message || "").toLowerCase().includes("once a day")) {
        result = legacyResult;
      }
    }
    return result;
  };

  const handleSave = async () => {
    if (!canSave) return;

    if (DISABLE_ALL_NETWORK) {
      if (pickerTab === "upload" && uploadFile) {
        const url = URL.createObjectURL(uploadFile);
        setUserDetails((prev) => ({ ...prev, profilepicture: url }));
      } else {
        setUserDetails((prev) => ({ ...prev, profilepicture: selected.src }));
      }
      alertSuccessMessage("Avatar updated (offline demo).");
      onAvatarCommitted?.(null);
      await onSaved?.();
      onClose();
      return;
    }

    LoaderHelper.loaderStatus(true);
    try {
      const fd = new FormData();
      let fallbackDisplay = selected.src;

      if (pickerTab === "upload" && uploadFile) {
        const fromName = uploadFile.name && /\.[a-z0-9]+$/i.test(uploadFile.name) ? uploadFile.name.replace(/^.*\./i, "").toLowerCase() : "";
        const extRaw =
          fromName ||
          (uploadFile.type === "image/png" ? "png" : uploadFile.type === "image/webp" ? "webp" : uploadFile.type === "image/gif" ? "gif" : "jpg");
        const ext = String(extRaw).replace(/[^a-z0-9]/gi, "") || "jpg";
        // Cropped uploads are always JPEG (avatar.jpg); GIF skip-crop keeps original type
        const safeName = uploadFile.type === "image/jpeg" && uploadFile.name === "avatar.jpg" ? "avatar.jpg" : `avatar.${ext}`;
        fd.append("profilepicture", uploadFile, safeName);
        fallbackDisplay = uploadPreviewUrl || URL.createObjectURL(uploadFile);
      } else {
        const imgRes = await fetch(selected.src, { mode: "cors", cache: "force-cache" });
        if (!imgRes.ok) {
          alertErrorMessage(`Could not load the selected avatar (${imgRes.status}). Try again or use Upload.`);
          return;
        }
        const blob = await imgRes.blob();
        if (!blob?.size) {
          alertErrorMessage("Could not read the selected avatar. Try Upload instead.");
          return;
        }
        const fileName = blob.type?.includes("png") ? "avatar.png" : "avatar.jpg";
        fd.append("profilepicture", blob, fileName);
      }

      const result = await uploadAvatarFormData(fd);
      if (isApiSuccess(result)) {
        const rel = await resolveAvatarPathAfterUpload(result);
        await applyAvatarSuccess(result, fallbackDisplay, rel);
      } else if (result?.message) {
        alertErrorMessage(result.message);
      } else {
        alertErrorMessage("Could not update avatar.");
      }
    } catch {
      alertErrorMessage("Could not update avatar. Check your file type (JPG, PNG, GIF, WebP) and size.");
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const onUploadPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!isLikelyAllowedAvatarFile(f)) {
      alertErrorMessage("Please choose a JPG, PNG, GIF, or WebP image.");
      e.target.value = "";
      return;
    }
    if (f.size > AVATAR_UPLOAD_MAX_BYTES) {
      alertErrorMessage(`Image must be ${AVATAR_UPLOAD_MAX_BYTES / (1024 * 1024)} MB or smaller.`);
      e.target.value = "";
      return;
    }

    // Animated GIF: skip crop (canvas would flatten) — use file as-is
    const isGif = /^image\/gif$/i.test(f.type) || /\.gif$/i.test(f.name || "");
    if (isGif) {
      setUploadFile(f);
      setUploadPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(f);
      });
      e.target.value = "";
      return;
    }

    const src = URL.createObjectURL(f);
    setCropSrc((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return src;
    });
    setCropOpen(true);
    e.target.value = "";
  };

  const onCropCancel = useCallback(() => {
    clearCropSrc();
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [clearCropSrc]);

  const onCropConfirm = useCallback(async () => {
    if (!cropPanelRef.current?.applyCrop || cropBusy) return;
    setCropBusy(true);
    try {
      const { file, previewUrl } = await cropPanelRef.current.applyCrop();
      setUploadFile(file);
      setUploadPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return previewUrl;
      });
      clearCropSrc();
      setPickerTab("upload");
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch {
      // Error already toasted in panel
    } finally {
      setCropBusy(false);
    }
  }, [clearCropSrc, cropBusy]);

  const handleShellClose = useCallback(() => {
    if (cropOpen) {
      onCropCancel();
      return;
    }
    onClose();
  }, [cropOpen, onCropCancel, onClose]);

  const footer = cropOpen ? (
    <>
      <button
        type="button"
        className="settings-modal__btn settings-modal__btn--muted"
        onClick={onCropCancel}
        disabled={cropBusy}
      >
        Cancel
      </button>
      <button
        type="button"
        className="settings-modal__btn settings-modal__btn--primary settings-modal__btn--wide"
        onClick={onCropConfirm}
        disabled={cropBusy}
      >
        {cropBusy ? "Processing…" : "Use photo"}
      </button>
    </>
  ) : (
    <>
      <button type="button" className="settings-modal__btn settings-modal__btn--muted" onClick={onClose}>
        Cancel
      </button>
      <button
        type="button"
        className="settings-modal__btn settings-modal__btn--primary settings-modal__btn--wide"
        disabled={!canSave}
        title={saveDisabledHint || undefined}
        onClick={handleSave}
      >
        Save
      </button>
    </>
  );

  return (
    <SettingsModalShell
      open={open}
      onClose={handleShellClose}
      title={cropOpen ? "Adjust photo" : "Edit Avatar"}
      footer={footer}
      modalClassName={`settings-modal--avatar${cropOpen ? " settings-modal--avatar-cropping" : ""}`}
    >
      {cropOpen && cropSrc ? (
        <AvatarCropPanel ref={cropPanelRef} imageSrc={cropSrc} busy={cropBusy} />
      ) : (
        <>
          <div className="settings-modal__info-banner" role="status">
            <i className="settings-modal__info-icon ri-information-fill" aria-hidden="true" />
            <p>
              Choose a gallery avatar or upload JPG, PNG, GIF, or WebP (max {AVATAR_UPLOAD_MAX_BYTES / (1024 * 1024)} MB).
              Uploaded photos can be cropped and adjusted before saving. If you use an NFT avatar and transfer it out,
              your profile may fall back to a default image.
            </p>
          </div>
          <div className="settings-modal__avatar-live-preview" aria-live="polite">
            <span className="settings-modal__avatar-live-preview-label">Preview</span>
            <div className="settings-modal__avatar-live-preview-frame">
              <img
                src={livePreviewSrc || DEFAULT_AVATAR_SRC}
                alt=""
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = DEFAULT_AVATAR_SRC;
                }}
              />
            </div>
          </div>
          <div className="settings-modal__tabs settings-modal__tabs--avatar-source" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={pickerTab === "gallery"}
              className={`settings-modal__tab ${pickerTab === "gallery" ? "is-active" : ""}`}
              onClick={() => setPickerTab("gallery")}
            >
              Gallery
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={pickerTab === "upload"}
              className={`settings-modal__tab ${pickerTab === "upload" ? "is-active" : ""}`}
              onClick={() => setPickerTab("upload")}
            >
              Upload
            </button>
          </div>

          {pickerTab === "gallery" ? (
            <>
              <div className="settings-modal__tabs" role="tablist">
                {["all", "male", "female"].map((key) => (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={filter === key}
                    className={`settings-modal__tab ${filter === key ? "is-active" : ""}`}
                    onClick={() => setFilter(key)}
                  >
                    {key === "all" ? "All" : key === "male" ? "Male" : "Female"}
                  </button>
                ))}
              </div>
              <div className="settings-modal__avatar-grid">
                {list.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className={`settings-modal__avatar-cell ${selectedId === a.id ? "is-selected" : ""}`}
                    onClick={() => setSelectedId(a.id)}
                    aria-pressed={selectedId === a.id}
                  >
                    <img src={a.src} alt="" />
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="settings-modal__avatar-upload">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp"
                className="settings-modal__avatar-upload-input visually-hidden"
                aria-label="Choose avatar image"
                onChange={onUploadPick}
              />
              <button
                type="button"
                className="settings-modal__avatar-upload-zone"
                onClick={() => fileInputRef.current?.click()}
              >
                {uploadPreviewUrl ? (
                  <img src={uploadPreviewUrl} alt="" className="settings-modal__avatar-upload-preview" />
                ) : (
                  <span className="settings-modal__avatar-upload-placeholder">
                    <i className="ri-image-add-line settings-modal__avatar-upload-icon" aria-hidden />
                    <span className="settings-modal__avatar-upload-text">Tap to choose an image</span>
                    <span className="settings-modal__avatar-upload-hint">
                      JPG, PNG, GIF, or WebP · max {(AVATAR_UPLOAD_MAX_BYTES / (1024 * 1024)).toFixed(0)} MB
                    </span>
                  </span>
                )}
              </button>
              {uploadFile ? (
                <div className="settings-modal__avatar-upload-actions">
                  <button
                    type="button"
                    className="settings-modal__avatar-upload-clear"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Change &amp; re-crop
                  </button>
                  <button
                    type="button"
                    className="settings-modal__avatar-upload-clear"
                    onClick={() => {
                      setUploadFile(null);
                      clearUploadPreview();
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                  >
                    Remove file
                  </button>
                </div>
              ) : null}
            </div>
          )}
        </>
      )}
    </SettingsModalShell>
  );
}

export function AlertsSettingModal({ open, onClose, initialTab }) {
  const [tab, setTab] = useState(initialTab);
  const [expanded, setExpanded] = useState("system");
  const [prefs, setPrefs] = useState(loadAlertPrefs);

  useEffect(() => {
    if (!open) return;
    setTab(initialTab);
    setExpanded("system");
    setPrefs(loadAlertPrefs());
  }, [open, initialTab]);

  useEffect(() => {
    if (!open || DISABLE_ALL_NETWORK) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await AuthService.getAlertSettingsSetting();
        if (cancelled) return;
        if (res?.success === false && res?.message) alertErrorMessage(res.message);
        const pair = alertSettingsPickData(res);
        if (!pair) return;
        const merged = {
          message: mergeAlertChannel(apiAlertChannelToUIPrefs(pair.messages)),
          email: mergeAlertChannel(apiAlertChannelToUIPrefs(pair.email)),
        };
        setPrefs(merged);
        try {
          localStorage.setItem(ALERT_PREFS_KEY, JSON.stringify(merged));
        } catch {
          // ignore
        }
      } catch {
        /* keep prefs from opening frame / localStorage */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const persist = useCallback((next) => {
    setPrefs(next);
    try {
      localStorage.setItem(ALERT_PREFS_KEY, JSON.stringify(next));
    } catch {
      // ignore
    }

    if (DISABLE_ALL_NETWORK) return;

    const body = {
      messages: uiPrefsToApiAlertChannel(next.message),
      email: uiPrefsToApiAlertChannel(next.email),
    };

    void AuthService.updateAlertSettingsSetting(body).then((res) => {
      const ok = res?.success === true || res?.success === 1;
      if (ok) {
        alertSuccessMessage(res?.message || "Alert settings saved.");
        return;
      }
      if (res?.success === false || res?.success === 0) alertErrorMessage(res?.message || "Could not save alert settings.");
    });
  }, []);

  const channel = tab === "message" ? "message" : "email";

  const toggleSystem = (id) => {
    const next = {
      ...prefs,
      [channel]: {
        ...prefs[channel],
        system: { ...prefs[channel].system, [id]: !prefs[channel].system[id] },
      },
    };
    persist(next);
  };

  const toggleExtra = (sectionId, keyId) => {
    const next = {
      ...prefs,
      [channel]: {
        ...prefs[channel],
        [sectionId]: {
          ...prefs[channel][sectionId],
          [keyId]: !prefs[channel][sectionId][keyId],
        },
      },
    };
    persist(next);
  };

  const sections = useMemo(
    () => [{ id: "system", title: "System Notifications", keys: SYSTEM_TOGGLES }, ...EXTRA_SECTIONS],
    []
  );

  return (
    <SettingsModalShell open={open} onClose={onClose} title="Alerts Setting" modalClassName="settings-modal--wide alert_setting">
      {/* <div className="settings-modal__alerts-tabs">
        <button
          type="button"
          className={`settings-modal__alerts-tab ${tab === "message" ? "is-active" : ""}`}
          onClick={() => setTab("message")}
        >
          Messages
        </button>
        <button
          type="button"
          className={`settings-modal__alerts-tab ${tab === "email" ? "is-active" : ""}`}
          onClick={() => setTab("email")}
        >
          Email
        </button>
      </div> */}
      <div className="settings-modal__accordion">
        {sections.map((sec) => {
          const isOpen = expanded === sec.id;
          return (
            <div key={sec.id} className="settings-modal__acc-item">
              <button
                type="button"
                className="settings-modal__acc-head"
                onClick={() => setExpanded(isOpen ? "" : sec.id)}
                aria-expanded={isOpen}
              >
                <span>{sec.title}</span>
                <i className={isOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} aria-hidden="true" />
              </button>
              {isOpen ? (
                <div className="settings-modal__acc-body">
                  {sec.id === "system"
                    ? sec.keys.map((row) => (
                        <div key={row.id} className="settings-modal__toggle-row">
                          <span>{row.label}</span>
                          <button
                            type="button"
                            className={`settings-modal__switch ${prefs[channel]?.system?.[row.id] ? "is-on" : ""}`}
                            onClick={() => toggleSystem(row.id)}
                            aria-pressed={!!prefs[channel]?.system?.[row.id]}
                          >
                            <span className="settings-modal__switch-knob" />
                          </button>
                        </div>
                      ))
                    : sec.keys.map((row) => (
                        <div key={row.id} className="settings-modal__toggle-row">
                          <span>{row.label}</span>
                          <button
                            type="button"
                            className={`settings-modal__switch ${prefs[channel]?.[sec.id]?.[row.id] ? "is-on" : ""}`}
                            onClick={() => toggleExtra(sec.id, row.id)}
                            aria-pressed={!!prefs[channel]?.[sec.id]?.[row.id]}
                          >
                            <span className="settings-modal__switch-knob" />
                          </button>
                        </div>
                      ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </SettingsModalShell>
  );
}

const NOTIFICATION_LANG_KEY = "agce_notification_language";

export const NOTIFICATION_LANGUAGES = [
  { id: "zh-CN", label: "简体中文" },
  { id: "en", label: "English" },
  { id: "vi", label: "Tiếng Việt" },
  { id: "zh-TW", label: "繁體中文" },
  { id: "es", label: "Español" },
  { id: "ru", label: "Русский" },
  { id: "fr", label: "Français (Afrique)" },
  { id: "pt-PT", label: "Português (Portugal)" },
  { id: "id", label: "Bahasa Indonesia" },
  { id: "ja", label: "日本語" },
  { id: "ar", label: "العربية" },
  { id: "uk", label: "Українська" },
];

function loadNotificationLanguage() {
  try {
    const v = localStorage.getItem(NOTIFICATION_LANG_KEY);
    if (v && NOTIFICATION_LANGUAGES.some((l) => l.id === v)) return v;
  } catch {
    // ignore
  }
  return "en";
}

export function getStoredNotificationLanguage() {
  return loadNotificationLanguage();
}

export function NotificationLanguageModal({ open, onClose }) {
  const [selected, setSelected] = useState(loadNotificationLanguage);

  useEffect(() => {
    if (open) setSelected(loadNotificationLanguage());
  }, [open]);

  const footer = (
    <>
      <button type="button" className="settings-modal__btn settings-modal__btn--muted" onClick={onClose}>
        Cancel
      </button>
      <button
        type="button"
        className="settings-modal__btn settings-modal__btn--primary settings-modal__btn--wide"
        onClick={() => {
          try {
            localStorage.setItem(NOTIFICATION_LANG_KEY, selected);
          } catch {
            // ignore
          }
          alertSuccessMessage("Notification language saved.");
          onClose();
        }}
      >
        Save
      </button>
    </>
  );

  return (
    <SettingsModalShell
      open={open}
      onClose={onClose}
      title="Notification Language"
      footer={footer}
      modalClassName="settings-modal--lang"
    >
      <ul className="settings-modal__lang-list" role="list">
        {NOTIFICATION_LANGUAGES.map((lang) => (
          <li key={lang.id} className="settings-modal__lang-row">
            <label className="settings-modal__lang-label">
              <input
                type="radio"
                name="notification-lang"
                value={lang.id}
                checked={selected === lang.id}
                onChange={() => setSelected(lang.id)}
              />
              <span>{lang.label}</span>
            </label>
          </li>
        ))}
      </ul>
    </SettingsModalShell>
  );
}
